import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, 
  Calendar, 
  FileText,
  Receipt,
  User,
  LogOut,
  Download,
  Clock,
  MapPin,
  CheckCircle,
  AlertCircle,
  ArrowLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [clientInfo, setClientInfo] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [reports, setReports] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const token = localStorage.getItem("client_token");

  const authConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    
    const stored = localStorage.getItem("client_info");
    if (stored) {
      setClientInfo(JSON.parse(stored));
    }
    
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [bookingsRes, reportsRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/client/bookings`, authConfig),
        axios.get(`${API}/client/reports`, authConfig),
        axios.get(`${API}/client/invoices`, authConfig)
      ]);
      
      setBookings(bookingsRes.data);
      setReports(reportsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("client_token");
        localStorage.removeItem("client_info");
        navigate("/login");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("client_token");
    localStorage.removeItem("client_info");
    toast.success("Logged out successfully");
    navigate("/");
  };

  const downloadReport = async (reportId, fileName) => {
    try {
      const response = await axios.get(`${API}/client/reports/${reportId}/download`, authConfig);
      
      // Decode base64 and create download
      const byteCharacters = atob(response.data.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Report downloaded");
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed": return "bg-green-500/20 text-green-400";
      case "completed": return "bg-emerald-500/20 text-emerald-400";
      case "pending": return "bg-yellow-500/20 text-yellow-400";
      case "cancelled": return "bg-red-500/20 text-red-400";
      case "paid": return "bg-green-500/20 text-green-400";
      case "unpaid": return "bg-yellow-500/20 text-yellow-400";
      case "overdue": return "bg-red-500/20 text-red-400";
      default: return "bg-neutral-500/20 text-neutral-400";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-black" data-testid="client-portal">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 bottom-0 w-64 bg-neutral-900/50 border-r border-neutral-800 p-6">
        <Link to="/">
          <img 
            src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
            alt="AIP Logo" 
            className="h-12 w-auto mb-8"
          />
        </Link>

        {clientInfo && (
          <div className="mb-8 p-4 bg-neutral-800/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#B8960B]/20 flex items-center justify-center">
                <User className="text-[#B8960B]" size={20} />
              </div>
              <div>
                <div className="font-medium text-sm">{clientInfo.name}</div>
                <div className="text-xs text-neutral-500">{clientInfo.email}</div>
              </div>
            </div>
          </div>
        )}

        <nav className="space-y-2">
          {[
            { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
            { id: "bookings", label: "My Bookings", icon: Calendar },
            { id: "reports", label: "Reports", icon: FileText },
            { id: "invoices", label: "Invoices", icon: Receipt }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id 
                  ? 'bg-[#B8960B]/20 text-[#B8960B]' 
                  : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
              }`}
              data-testid={`portal-tab-${item.id}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6 space-y-2">
          <Link to="/">
            <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-400 hover:bg-neutral-800 hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span>Back to Site</span>
            </button>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-neutral-400 hover:bg-neutral-800 hover:text-white transition-colors"
          >
            <LogOut size={20} />
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 p-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-neutral-500">Loading...</div>
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === "dashboard" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <h1 className="text-2xl font-bold mb-2">Welcome back, {clientInfo?.name?.split(' ')[0]}!</h1>
                <p className="text-neutral-400 mb-8">Here's an overview of your account</p>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Calendar className="text-blue-400" size={24} />
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{bookings.length}</div>
                        <div className="text-neutral-500 text-sm">Total Bookings</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                        <FileText className="text-green-400" size={24} />
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{reports.length}</div>
                        <div className="text-neutral-500 text-sm">Reports Available</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#B8960B]/20 flex items-center justify-center">
                        <Receipt className="text-[#B8960B]" size={24} />
                      </div>
                      <div>
                        <div className="text-2xl font-bold">{invoices.filter(i => i.status === 'unpaid').length}</div>
                        <div className="text-neutral-500 text-sm">Unpaid Invoices</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                    <h3 className="font-semibold mb-4">Recent Bookings</h3>
                    {bookings.slice(0, 3).map((booking, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg mb-2">
                        <div>
                          <div className="font-medium text-sm">{booking.booking_date}</div>
                          <div className="text-xs text-neutral-500">{booking.property_address}</div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                      </div>
                    ))}
                    {bookings.length === 0 && (
                      <p className="text-neutral-500 text-sm">No bookings yet</p>
                    )}
                  </div>

                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                    <h3 className="font-semibold mb-4">Recent Reports</h3>
                    {reports.slice(0, 3).map((report, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-neutral-800/50 rounded-lg mb-2">
                        <div>
                          <div className="font-medium text-sm">{report.title}</div>
                          <div className="text-xs text-neutral-500">{report.property_address}</div>
                        </div>
                        <button
                          onClick={() => downloadReport(report.id, report.file_name)}
                          className="p-2 text-[#B8960B] hover:bg-[#B8960B]/20 rounded transition-colors"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    ))}
                    {reports.length === 0 && (
                      <p className="text-neutral-500 text-sm">No reports available yet</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Bookings Tab */}
            {activeTab === "bookings" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h1 className="text-2xl font-bold">My Bookings</h1>
                    <p className="text-neutral-400">View all your past and upcoming bookings</p>
                  </div>
                  <Link to="/book">
                    <Button className="btn-primary rounded-full">
                      Book New Inspection
                    </Button>
                  </Link>
                </div>

                {bookings.length === 0 ? (
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-12 text-center">
                    <Calendar className="mx-auto text-neutral-600 mb-4" size={48} />
                    <h3 className="text-lg font-medium mb-2">No bookings yet</h3>
                    <p className="text-neutral-500 mb-6">Book your first property inspection</p>
                    <Link to="/book">
                      <Button className="btn-primary rounded-full">Book Now</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bookings.map((booking, i) => (
                      <div key={i} className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-lg bg-[#B8960B]/20 flex items-center justify-center flex-shrink-0">
                              <Calendar className="text-[#B8960B]" size={24} />
                            </div>
                            <div>
                              <div className="font-medium">{booking.property_address}</div>
                              <div className="text-sm text-neutral-500 flex items-center gap-4 mt-1">
                                <span className="flex items-center gap-1">
                                  <Clock size={14} />
                                  {booking.booking_date} at {booking.booking_time}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin size={14} />
                                  {booking.postcode}
                                </span>
                              </div>
                              <div className="text-sm text-neutral-500 mt-1">
                                Services: {booking.services?.join(", ") || "-"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {booking.quoted_price > 0 && (
                              <div className="text-[#B8960B] font-semibold">
                                £{booking.quoted_price?.toFixed(2)}
                              </div>
                            )}
                            <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(booking.status)}`}>
                              {booking.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Reports Tab */}
            {activeTab === "reports" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <h1 className="text-2xl font-bold mb-2">My Reports</h1>
                <p className="text-neutral-400 mb-8">Download your property inventory reports</p>

                {reports.length === 0 ? (
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-12 text-center">
                    <FileText className="mx-auto text-neutral-600 mb-4" size={48} />
                    <h3 className="text-lg font-medium mb-2">No reports yet</h3>
                    <p className="text-neutral-500">Your inventory reports will appear here once completed</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {reports.map((report, i) => (
                      <div key={i} className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                            <FileText className="text-green-400" size={24} />
                          </div>
                          <span className="text-xs text-neutral-500 bg-neutral-800 px-2 py-1 rounded">
                            {report.report_type}
                          </span>
                        </div>
                        <h3 className="font-medium mb-1">{report.title}</h3>
                        <p className="text-sm text-neutral-500 mb-1">{report.property_address}</p>
                        <p className="text-xs text-neutral-600 mb-4">{formatDate(report.uploaded_at)}</p>
                        <Button
                          onClick={() => downloadReport(report.id, report.file_name)}
                          className="w-full btn-secondary rounded-lg"
                        >
                          <Download size={16} className="mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Invoices Tab */}
            {activeTab === "invoices" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <h1 className="text-2xl font-bold mb-2">My Invoices</h1>
                <p className="text-neutral-400 mb-8">View and manage your invoices</p>

                {invoices.length === 0 ? (
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-12 text-center">
                    <Receipt className="mx-auto text-neutral-600 mb-4" size={48} />
                    <h3 className="text-lg font-medium mb-2">No invoices yet</h3>
                    <p className="text-neutral-500">Your invoices will appear here</p>
                  </div>
                ) : (
                  <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-neutral-800/50">
                        <tr>
                          <th className="text-left p-4 text-sm text-neutral-400">Invoice #</th>
                          <th className="text-left p-4 text-sm text-neutral-400">Description</th>
                          <th className="text-left p-4 text-sm text-neutral-400">Amount</th>
                          <th className="text-left p-4 text-sm text-neutral-400">Due Date</th>
                          <th className="text-left p-4 text-sm text-neutral-400">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice, i) => (
                          <tr key={i} className="border-t border-neutral-800">
                            <td className="p-4 font-mono text-sm">{invoice.invoice_number}</td>
                            <td className="p-4 text-sm">{invoice.description}</td>
                            <td className="p-4 text-sm font-medium text-[#B8960B]">£{invoice.amount?.toFixed(2)}</td>
                            <td className="p-4 text-sm text-neutral-500">{invoice.due_date}</td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded text-xs ${getStatusColor(invoice.status)}`}>
                                {invoice.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
